import { Fragment, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';
import classnames from 'classnames/bind';
import { useSelector, useDispatch } from 'app/lib/hooks';
import { selectSnowflakeConnection, selectMappedSnowflakeStatus } from 'app/store/healthcheck';
import {
    selectExecutingQueryCount,
    selectIsExecuting,
    selectQueryCount
} from 'app/store/snowflakeQueries';
import {
    doToggleConnectionStatusModal,
    selectIsConnectionStatusModalOpen,
    ERROR_CODE_SEARCH_PARAM,
    ERROR_DESCRIPTION_SEARCH_PARAM
} from 'app/store/snowflakeConfiguration';
import { Button, ButtonVariant } from 'app/components/core/Button';
import { useAccessibleTooltip } from 'app/components/core/AccessibleTooltip';
import { SnowflakeConnectionState } from 'app/lib/types/server';
import { Defaults } from 'app/lib/constants';
import { Icon, IconSize } from 'app/components/core/Icon';
import { StatusIndicator } from './StatusIndicator.jsx';
import { StatusTooltip } from './StatusTooltip.jsx';
import { SnowflakeConnectionModal } from './SnowflakeConnectionModal.jsx';
import * as styles from './SnowflakeConnectionStatus.css';

const BUTTON_ID = 'snowflake-connection-status-button';

const cx = classnames.bind(styles);

const DEFAULT_CONNECTION = {
    state: SnowflakeConnectionState.DISCONNECTED
};

export function SnowflakeConnectionStatus() {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const snowflakeConnection = useSelector(selectSnowflakeConnection) || DEFAULT_CONNECTION;
    const mappedSnowflakeStatus = useSelector(selectMappedSnowflakeStatus) || Defaults.OBJECT;
    const isModalOpen = useSelector(selectIsConnectionStatusModalOpen);
    const isExecuting = useSelector(selectIsExecuting);
    const queryCount = useSelector(selectQueryCount);
    const executingQueryCount = useSelector(selectExecutingQueryCount);

    const onToggleConnectionStatusModal = useCallback(
        (value) => dispatch(doToggleConnectionStatusModal(value)),
        [dispatch]
    );

    const { Tooltip, getTooltipProps, getReferenceProps } = useAccessibleTooltip({
        placement: 'bottom'
    });

    const errorCode = searchParams.get(ERROR_CODE_SEARCH_PARAM);
    const errorDescription = searchParams.get(ERROR_DESCRIPTION_SEARCH_PARAM);

    useEffect(() => {
        if (errorCode) {
            onToggleConnectionStatusModal(true);
        }
    }, [errorCode, errorDescription, onToggleConnectionStatusModal]);

    if (mappedSnowflakeStatus.noStatus) {
        return null;
    }

    const { account, user, reason } = snowflakeConnection;
    const { isError, isSignInRequired } = mappedSnowflakeStatus;

    const pulseButtonClasses = cx('statusButtonContainer', {
        'pulse-error': isError && !isModalOpen,
        'pulse-sign-in-required': isSignInRequired && !isModalOpen
    });

    const renderButton = () => (
        <Button
            variant={ButtonVariant.GHOST}
            data-testid="snowflakeStatus"
            className={pulseButtonClasses}
            id={BUTTON_ID}
            onClick={() => onToggleConnectionStatusModal(true)}
            aria-haspopup="true"
            aria-controls={BUTTON_ID}
            aria-expanded={isModalOpen}
            {...getReferenceProps()}>
            {isExecuting && (
                <svg className={styles.loading} viewBox="0 0 32 32" width={32} height={32}>
                    <g>
                        <circle cx="16" cy="16" r="14" />
                    </g>
                </svg>
            )}
            <Icon variant="snowflake" size={IconSize.SMALL} inline />
            <StatusIndicator mappedSnowflakeStatus={mappedSnowflakeStatus} />
            <Icon variant="chevron-down" size={IconSize.EXTRA_SMALL} />
            {!isModalOpen && (
                <Tooltip {...getTooltipProps()}>
                    <StatusTooltip
                        mappedSnowflakeStatus={mappedSnowflakeStatus}
                        snowflakeConnection={snowflakeConnection}
                        queryCount={queryCount}
                        executingQueryCount={executingQueryCount}
                    />
                </Tooltip>
            )}
        </Button>
    );

    return (
        <Fragment>
            {renderButton()}
            {isModalOpen && (
                <SnowflakeConnectionModal
                    attachTo={BUTTON_ID}
                    onClose={() => onToggleConnectionStatusModal(false)}
                    account={account}
                    reason={reason}
                    user={user}
                    mappedSnowflakeStatus={mappedSnowflakeStatus}
                />
            )}
        </Fragment>
    );
}
